import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import KanbanBoard from '../components/KanbanBoard';
import CardDetailModal from '../components/CardDetailModal';
import { Building2, Calendar, Archive, Plus, X, User, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BoardView = () => {
  const { boardId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [board, setBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [streams, setStreams] = useState([]);
  const [cards, setCards] = useState([]);
  const [retiredCards, setRetiredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialCardId, setInitialCardId] = useState(null);
  const [activeTab, setActiveTab] = useState('live'); // 'live' or 'retired'
  const [selectedRetiredCard, setSelectedRetiredCard] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newCompanyDescription, setNewCompanyDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [ownershipFilter, setOwnershipFilter] = useState('all'); // 'all' or 'mine'

  useEffect(() => {
    // Check for card query param to auto-open a card
    const cardId = searchParams.get('card');
    if (cardId) {
      setInitialCardId(cardId);
      // Clear the query param after reading it
      setSearchParams({});
    }
    fetchCurrentUser();
    fetchBoardData();
  }, [boardId]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchBoardData = async () => {
    try {
      const [boardRes, columnsRes, streamsRes, cardsRes, retiredRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/boards/${boardId}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/boards/${boardId}/columns`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/boards/${boardId}/streams`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/boards/${boardId}/cards`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/boards/${boardId}/cards/retired`, { credentials: 'include' })
      ]);

      if (boardRes.ok && columnsRes.ok && streamsRes.ok && cardsRes.ok) {
        setBoard(await boardRes.json());
        setColumns(await columnsRes.json());
        setStreams(await streamsRes.json());
        setCards(await cardsRes.json());
      }
      if (retiredRes.ok) {
        setRetiredCards(await retiredRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch board data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardMove = async (cardId, newColumnId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${cardId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ column_id: newColumnId })
      });

      if (response.ok) {
        fetchBoardData();
      }
    } catch (error) {
      console.error('Failed to move card:', error);
    }
  };

  // Clear initialCardId after it's been used
  const handleCardOpened = () => {
    setInitialCardId(null);
  };

  // Check for duplicate companies when typing
  const checkDuplicates = async (companyName) => {
    if (companyName.length < 2) {
      setDuplicateMatches([]);
      return;
    }
    setCheckingDuplicates(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/cards/search-duplicates?company_name=${encodeURIComponent(companyName)}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const matches = await response.json();
        setDuplicateMatches(matches);
      }
    } catch (error) {
      console.error('Failed to check duplicates:', error);
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // Handle company name input with debounced duplicate check
  const handleCompanyNameChange = (value) => {
    setNewCompanyName(value);
    // Debounce the duplicate check
    const timeoutId = setTimeout(() => checkDuplicates(value), 300);
    return () => clearTimeout(timeoutId);
  };

  // Create new card
  const handleCreateCard = async () => {
    if (!newCompanyName.trim()) return;
    
    setCreating(true);
    try {
      // Find "New Lead" column or first column
      const newLeadColumn = columns.find(c => c.name === 'New Lead') || columns[0];
      if (!newLeadColumn) {
        alert('No columns found in board');
        return;
      }

      // Get first stream or use empty string
      const defaultStream = streams.length > 0 ? streams[0].stream_id : '';

      const response = await fetch(`${BACKEND_URL}/api/boards/${boardId}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newCompanyName.trim(),
          description: newCompanyDescription.trim() || null,
          column_id: newLeadColumn.column_id,
          stream_id: defaultStream
        })
      });

      if (response.ok) {
        setShowCreateModal(false);
        setNewCompanyName('');
        setNewCompanyDescription('');
        setDuplicateMatches([]);
        fetchBoardData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create card');
      }
    } catch (error) {
      console.error('Failed to create card:', error);
      alert('Failed to create card');
    } finally {
      setCreating(false);
    }
  };

  // Reinstate a duplicate card instead of creating new
  const handleReinstateFromCreate = async (cardId) => {
    setCreating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cards/${cardId}/reinstate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setShowCreateModal(false);
        setNewCompanyName('');
        setNewCompanyDescription('');
        setDuplicateMatches([]);
        fetchBoardData();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reinstate card');
      }
    } catch (error) {
      console.error('Failed to reinstate card:', error);
      alert('Failed to reinstate card');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getRetirementReasonLabel = (reason) => {
    const labels = {
      rejected: 'Rejected',
      no_budget: 'No Budget',
      not_right_time: 'Not the Right Time',
      lost_to_competitor: 'Lost to Competitor',
      no_response: 'No Response',
      project_cancelled: 'Project Cancelled',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <AppLayout title="Loading...">
        <div className="text-[#A2FF00]">Loading board...</div>
      </AppLayout>
    );
  }

  const isMemberTracker = board?.board_type === 'member_tracking';

  // Filter cards based on ownership
  const filteredCards = ownershipFilter === 'mine' && currentUser
    ? cards.filter(card => card.relationship_owner_id === currentUser.user_id)
    : cards;

  const myCardsCount = currentUser 
    ? cards.filter(card => card.relationship_owner_id === currentUser.user_id).length 
    : 0;

  return (
    <AppLayout title={board?.name || 'Board'}>
      {/* Header with Tabs and Add Button - Only for Member Tracker */}
      {isMemberTracker && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            {/* Live/Retired Tabs */}
            <div className={`flex gap-1 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} p-1 rounded-sm`}>
              <button
                onClick={() => setActiveTab('live')}
                className={`px-6 py-2 text-sm font-medium rounded-sm transition-colors ${
                  activeTab === 'live'
                    ? `${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black`
                    : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`
                }`}
              >
                Live ({filteredCards.length})
              </button>
              <button
                onClick={() => setActiveTab('retired')}
                className={`px-6 py-2 text-sm font-medium rounded-sm transition-colors ${
                  activeTab === 'retired'
                    ? 'bg-orange-500 text-white'
                    : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`
                }`}
              >
                Retired ({retiredCards.length})
              </button>
            </div>

            {/* My Cards / All Cards Filter - Only show in Live view */}
            {activeTab === 'live' && (
              <div className={`flex gap-1 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} p-1 rounded-sm`}>
                <button
                  onClick={() => setOwnershipFilter('all')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    ownershipFilter === 'all'
                      ? `${isDark ? 'bg-blue-500' : 'bg-blue-500'} text-white`
                      : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`
                  }`}
                >
                  <Users className="w-4 h-4" />
                  All Cards ({cards.length})
                </button>
                <button
                  onClick={() => setOwnershipFilter('mine')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                    ownershipFilter === 'mine'
                      ? `${isDark ? 'bg-blue-500' : 'bg-blue-500'} text-white`
                      : `${isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`
                  }`}
                >
                  <User className="w-4 h-4" />
                  My Cards ({myCardsCount})
                </button>
              </div>
            )}
          </div>
          
          {/* Add Company Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black font-bold rounded-sm ${isDark ? 'hover:bg-[#8EFF00]' : 'hover:bg-green-600'} transition-colors`}
          >
            <Plus className="w-4 h-4" />
            Add Company
          </button>
        </div>
      )}

      {/* Live View - Kanban Board */}
      {activeTab === 'live' && (
        <KanbanBoard
          columns={columns}
          streams={streams}
          cards={filteredCards}
          onCardMove={handleCardMove}
          onRefresh={fetchBoardData}
          boardType={board?.board_type}
          initialCardId={initialCardId}
          onCardOpened={handleCardOpened}
        />
      )}

      {/* Retired View - List View */}
      {activeTab === 'retired' && isMemberTracker && (
        <div>
          {retiredCards.length === 0 ? (
            <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-8 text-center`}>
              <Archive className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No retired cards</p>
            </div>
          ) : (
            <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm overflow-hidden`}>
              <table className="w-full">
                <thead className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <tr>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Company</th>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Retirement Reason</th>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Retired From</th>
                    <th className="text-left p-4 text-gray-400 font-medium text-sm">Date Retired</th>
                  </tr>
                </thead>
                <tbody>
                  {retiredCards.map((card) => {
                    const retiredFromColumn = columns.find(c => c.column_id === card.retired_from_column_id);
                    return (
                      <tr
                        key={card.card_id}
                        onClick={() => setSelectedRetiredCard(card)}
                        className={`border-b ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer transition-colors`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-sm bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-orange-400" />
                            </div>
                            <div>
                              <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{card.title}</div>
                              {card.description && (
                                <div className="text-xs text-gray-500 truncate max-w-[250px]">
                                  {card.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-400">
                            {getRetirementReasonLabel(card.retired_reason)}
                          </span>
                        </td>
                        <td className="p-4 text-gray-400 text-sm">
                          {retiredFromColumn?.name || 'Unknown'}
                        </td>
                        <td className="p-4">
                          <div className="text-gray-400 text-sm flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(card.retired_at)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Card Detail Modal for Retired Cards */}
          {selectedRetiredCard && (
            <CardDetailModal
              card={selectedRetiredCard}
              isOpen={!!selectedRetiredCard}
              onClose={() => setSelectedRetiredCard(null)}
              onUpdate={fetchBoardData}
              boardType={board?.board_type}
              columnName={null}
            />
          )}
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : 'bg-white border-gray-300'} border rounded-sm w-full max-w-lg`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Building2 className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                Add New Company
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCompanyName('');
                  setNewCompanyDescription('');
                  setDuplicateMatches([]);
                }}
                className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => handleCompanyNameChange(e.target.value)}
                  className={`w-full ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm px-4 py-3 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none`}
                  placeholder="Enter company name"
                  autoFocus
                />
              </div>

              {/* Duplicate Warning */}
              {duplicateMatches.length > 0 && (
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-sm p-4">
                  <p className="text-orange-400 font-medium mb-2">
                    This company may already exist. Click to view or reinstate:
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {duplicateMatches.map((match) => (
                      <div
                        key={match.card_id}
                        className={`flex items-center justify-between ${isDark ? 'bg-black/50 hover:bg-black/70' : 'bg-white hover:bg-gray-50'} p-3 rounded-sm cursor-pointer transition-colors`}
                        onClick={() => {
                          setShowCreateModal(false);
                          setNewCompanyName('');
                          setNewCompanyDescription('');
                          setDuplicateMatches([]);
                          // Navigate to the board with the card query param to open the card modal
                          navigate(`/board/${match.board_id}?card=${match.card_id}`);
                        }}
                      >
                        <div className="flex-1">
                          <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium hover:text-[#A2FF00] transition-colors`}>
                            {match.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {match.is_retired ? (
                              <span className="text-orange-400">Retired</span>
                            ) : (
                              <span className="text-green-400">Active</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {match.is_retired ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReinstateFromCreate(match.card_id);
                              }}
                              disabled={creating}
                              className={`px-3 py-1 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black text-sm font-bold rounded-sm ${isDark ? 'hover:bg-[#8EFF00]' : 'hover:bg-green-600'} disabled:opacity-50`}
                            >
                              Reinstate
                            </button>
                          ) : (
                            <span className={`px-3 py-1 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'} text-sm rounded-sm`}>
                              View →
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                  Description (Optional)
                </label>
                <textarea
                  value={newCompanyDescription}
                  onChange={(e) => setNewCompanyDescription(e.target.value)}
                  className={`w-full ${isDark ? 'bg-black border-white/20 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border rounded-sm px-4 py-3 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] outline-none resize-none`}
                  placeholder="Brief description of the company"
                  rows={3}
                />
              </div>

              <p className="text-xs text-gray-500">
                New companies will be added to the &quot;New Lead&quot; column.
              </p>
            </div>

            {/* Modal Footer */}
            <div className={`flex items-center justify-end gap-3 p-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewCompanyName('');
                  setNewCompanyDescription('');
                  setDuplicateMatches([]);
                }}
                className={`px-4 py-2 border ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCard}
                disabled={creating || !newCompanyName.trim()}
                className={`px-6 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
              >
                <Plus className="w-4 h-4" />
                {creating ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default BoardView;
